import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { MedicationTemplate } from '@/types';
import { supabase } from '@/lib/supabase';
import { generatePrescriptionId } from '@/lib/utils/prescriptionId';
import { getCurrentHospitalId } from '@/lib/hospitalContext';

// Define the store state interface
interface MedicationTemplatesState {
  templates: MedicationTemplate[];
  
  // Actions
  addTemplate: (template: Omit<MedicationTemplate, 'id' | 'createdAt' | 'updatedAt' | 'usageCount'>) => string;
  updateTemplate: (template: MedicationTemplate) => void;
  removeTemplate: (id: string) => void;
  incrementUsage: (id: string) => void;
  clearTemplates: () => void;
  
  // Selectors
  getTemplatesByDoctor: (doctorId: string) => MedicationTemplate[];
  getTemplateById: (id: string) => MedicationTemplate | undefined;
  searchTemplates: (doctorId: string, searchTerm: string) => MedicationTemplate[];
  
  // Fetch templates from Supabase
  fetchTemplates: (doctorId: string) => Promise<void>;
}

// Create a function to get user-specific storage key
const getStorageKey = (userId: string) => `medication-templates-storage-${userId}`;

export const useMedicationTemplatesStore = create<MedicationTemplatesState>()(
  persist(
    (set, get) => ({
      templates: [],
      
      // Add a new template
      addTemplate: (templateData) => {
        const currentTemplates = get().templates;
        const id = generatePrescriptionId(currentTemplates.map(t => ({ id: t.id, created_at: t.createdAt })));
        const newTemplate: MedicationTemplate = {
          ...templateData,
          id,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          usageCount: 0
        };
        
        set((state) => ({
          templates: [...state.templates, newTemplate]
        }));
        
        return id;
      },
      
      // Update an existing template
      updateTemplate: (updatedTemplate) => {
        set((state) => ({
          templates: state.templates.map((template) =>
            template.id === updatedTemplate.id 
              ? { ...updatedTemplate, updatedAt: new Date().toISOString() }
              : template
          )
        }));
      },
      
      // Remove a template by ID
      removeTemplate: (id) => {
        set((state) => ({
          templates: state.templates.filter((template) => template.id !== id)
        }));
      },
      
      // Increment usage count
      incrementUsage: (id) => {
        set((state) => ({
          templates: state.templates.map((template) =>
            template.id === id 
              ? { 
                  ...template, 
                  usageCount: template.usageCount + 1,
                  lastUsed: new Date().toISOString(),
                  updatedAt: new Date().toISOString()
                }
              : template
          )
        }));
      },
      
      // Get templates by doctor ID
      getTemplatesByDoctor: (doctorId) => {
        const { templates } = get();
        return templates
          .filter(template => template.doctorId === doctorId)
          .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      },
      
      // Get a single template by ID
      getTemplateById: (id) => {
        const { templates } = get();
        return templates.find(template => template.id === id);
      },
      
      // Search templates by diagnosis or template name
      searchTemplates: (doctorId, searchTerm) => {
        const { templates } = get();
        const doctorTemplates = templates.filter(template => template.doctorId === doctorId);
        
        if (!searchTerm.trim()) {
          return doctorTemplates;
        }
        
        const term = searchTerm.toLowerCase();
        return doctorTemplates.filter(template =>
          template.diagnosis.toLowerCase().includes(term) ||
          template.templateName.toLowerCase().includes(term) ||
          template.medications.some(med => med.name.toLowerCase().includes(term))
        );
      },
      
      // Fetch templates from Supabase
      fetchTemplates: async (doctorId) => {
        try {
          const { data, error } = await supabase
            .from('medication_templates')
            .select('*')
            .eq('doctor_id', doctorId)
            .order('updated_at', { ascending: false });
            
          if (error) {
            console.error('Error fetching templates:', error);
            throw error;
          }
          
          // Transform data to match our interface
          const transformedTemplates = data?.map((item: any) => ({
            id: item.id,
            doctorId: item.doctor_id,
            diagnosis: item.diagnosis,
            templateName: item.template_name,
            medications: typeof item.medications === 'string' ? JSON.parse(item.medications) : item.medications,
            createdAt: item.created_at,
            updatedAt: item.updated_at,
            usageCount: item.usage_count || 0,
            lastUsed: item.last_used
          })) || [];
          
          set({ templates: transformedTemplates });
        } catch (error) {
          console.error('Error in fetchTemplates:', error);
          throw error;
        }
      },
      
      // Clear templates (useful for switching users)
      clearTemplates: () => {
        set({ templates: [] });
      },
    }),
    {
      name: 'medication-templates-storage',
      // Don't persist templates to localStorage - always fetch from database
      skipHydration: true,
    }
  )
);

// Save template to backend
export async function saveMedicationTemplate(templateData: {
  doctorId: string;
  diagnosis: string;
  templateName: string;
  medications: any[];
}) {
  try {
    console.log('Saving template with data:', templateData);
    
    // Check if template with same diagnosis and name already exists
    // Handle RLS policy failures gracefully
    // Note: We check in the store first to avoid 406 errors from Supabase queries with spaces
    try {
      const { templates } = useMedicationTemplatesStore.getState();
      const existingTemplate = templates.find(
        t => 
          t.doctorId === templateData.doctorId &&
          t.diagnosis === templateData.diagnosis &&
          t.templateName === templateData.templateName
      );

      if (existingTemplate) {
        throw new Error('A template with this diagnosis and name already exists');
      }
    } catch (error: any) {
      // If it's our custom error, throw it
      if (error.message && error.message.includes('already exists')) {
        throw error;
      }
      // If duplicate check fails for any other reason, log and continue
      // This prevents the 406 error from blocking template creation
      console.log('Duplicate check failed, continuing with insert:', error);
    }

    // Get current hospital ID (required by RLS policy)
    const currentHospitalId = await getCurrentHospitalId();
    if (!currentHospitalId) {
      throw new Error('No hospital context available. Please ensure you are logged into a hospital.');
    }

    // Database auto-generates UUID for id field (don't provide custom ID)
    // Create insert data (let database generate UUID)
    // Note: Database schema requires 'name', 'dosage', 'frequency', 'duration' (NOT NULL)
    // Extract values from first medication if medications array exists, otherwise use defaults
    const firstMedication = templateData.medications && templateData.medications.length > 0 
      ? templateData.medications[0] 
      : null;
    
    const insertData: any = {
      // id is auto-generated by database as UUID (not provided)
      doctor_id: templateData.doctorId,
      hospital_id: currentHospitalId, // Required by RLS policy
      name: templateData.templateName, // Required NOT NULL column
      dosage: firstMedication?.dosage || '1', // Required NOT NULL - extract from first medication
      frequency: firstMedication?.frequency || 'Once daily', // Required NOT NULL
      duration: firstMedication?.duration || '7 days', // Required NOT NULL
      diagnosis: templateData.diagnosis, // Include if column exists
      template_name: templateData.templateName, // Include if column exists
      medications: JSON.stringify(templateData.medications), // Include if column exists (JSONB)
      usage_count: 0, // Include if column exists
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    console.log('Insert data:', insertData);

    const { data, error } = await supabase
      .from('medication_templates')
      .insert([insertData])
      .select()
      .single();

    if (error) {
      console.error('Error saving template:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in saveMedicationTemplate:', error);
    throw error;
  }
}

// Load template medications
export async function loadTemplateMedications(templateId: string) {
  try {
    const { data, error } = await supabase
      .from('medication_templates')
      .select('medications, usage_count')
      .eq('id', templateId)
      .single();

    if (error) {
      console.error('Error loading template:', error);
      throw error;
    }

    // Increment usage count
    await supabase
      .from('medication_templates')
      .update({ 
        usage_count: (data.usage_count || 0) + 1,
        last_used: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', templateId);

    const medications = typeof data.medications === 'string' ? JSON.parse(data.medications) : data.medications;

    return medications;
  } catch (error) {
    console.error('Error in loadTemplateMedications:', error);
    throw error;
  }
}

// Delete template
export async function deleteMedicationTemplate(templateId: string) {
  try {
    const { error } = await supabase
      .from('medication_templates')
      .delete()
      .eq('id', templateId);

    if (error) {
      console.error('Error deleting template:', error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Error in deleteMedicationTemplate:', error);
    throw error;
  }
}

// Update template
export async function updateMedicationTemplate(templateId: string, updates: {
  diagnosis?: string;
  templateName?: string;
  medications?: any[];
}) {
  try {
    const updateData: any = {
      updated_at: new Date().toISOString()
    };

    if (updates.diagnosis !== undefined) updateData.diagnosis = updates.diagnosis;
    if (updates.templateName !== undefined) updateData.template_name = updates.templateName;
    if (updates.medications !== undefined) updateData.medications = JSON.stringify(updates.medications);

    const { data, error } = await supabase
      .from('medication_templates')
      .update(updateData)
      .eq('id', templateId)
      .select();

    if (error) {
      console.error('Error updating template:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in updateMedicationTemplate:', error);
    throw error;
  }
}
